import { HttpClient, HttpParams, HttpResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { EMPTY, forkJoin, Observable, of } from "rxjs";
import { expand, finalize, map, reduce, switchMap, takeUntil, tap } from "rxjs/operators";
import { OverlayService } from "../components/overlay/service/overlay.service";
import { CancelService } from "./cancel/cancel.service";
import { LoggerService } from "./logger.service";

@Injectable({
	providedIn: "root",
})
export class ServiceUtilsService {
	private PER_PAGE = 500;

	// this service exists for utilities that need the injected resources from Angular

	constructor(
		private http: HttpClient,
		public logger: LoggerService,
		public overlayService: OverlayService,
		private cancelService: CancelService,
	) {}

	// any return type since it handles any api request with multiple pages
	public getAllPages<T = unknown>(
		uri: string,
		entityName: string = "entities",
		pageSize: number = 1000,
		startingPage: number = 1,
		perPageKey = "perPage",
	): Observable<T[]> {
		let page = startingPage;
		this.overlayService.open();

		this.logger.log(`Beginning retrieval of ${entityName}: ${uri}`);

		return this.http.get<T[]>(`${uri}&page=${page}&${perPageKey}=${pageSize}`).pipe(
			takeUntil(this.cancelService.cancel$),
			expand(response => {
				if (response.length === pageSize) {
					this.logger.log(`Retrieved page ${page} of ${entityName}`);
					page++;
					this.logger.log(`Requesting page ${page} of ${entityName}`);

					return this.http
						.get<T[]>(`${uri}&page=${page}&${perPageKey}=${pageSize}`)
						.pipe(takeUntil(this.cancelService.cancel$));
				}

				if (response.length === 0) {
					if (page === startingPage) {
						this.logger.log(`No results returned for ${entityName}`);
					} else {
						this.logger.log(`No results returned for page ${page} of ${entityName}, ending stream`);
					}
					return EMPTY;
				}

				this.logger.log(`Reached last page (${page}) of ${entityName}`);
				return EMPTY;
			}),
			takeUntil(this.cancelService.cancel$),
			reduce((allResults, currentPage) => [...allResults, ...currentPage], [] as T[]),
			tap(allEntities => {
				this.logger.log(`Total number of ${entityName} retrieved: ${allEntities.length}`);
			}),
			finalize(() => this.overlayService.close()),
		);
	}

	getAll(url, params, per_page = this.PER_PAGE): Observable<any[]> {
		const firstPageParams: HttpParams = new HttpParams({
			fromObject: { ...params, page: 1, per_page: per_page },
		});
		const firstPage = this.http.get(url, {
			params: firstPageParams,
			observe: "response",
		});

		return firstPage.pipe(
			switchMap(res => {
				const pageCount = +res.headers.get("x-page-count");
				const remainingPages = [];
				for (let i = 2; i <= pageCount; i++) {
					const nextPageParams: HttpParams = new HttpParams({
						fromObject: { ...params, page: i, per_page },
					});
					const nextPage = this.http.get(url, { params: nextPageParams, observe: "response" });
					remainingPages.push(nextPage);
				}
				return forkJoin([of(res), ...remainingPages]) as Observable<Array<HttpResponse<any>>>;
			}),
			map(allResponses => {
				return allResponses.reduce((acc, res) => [...acc, ...res.body], []);
			}),
		);
	}
}
